import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import { motion } from 'motion/react';
import {
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  Layers,
  FileText,
  LineChart,
  Ship,
  BarChart3,
  Bot,
  BadgeDollarSign,
  Truck,
  Monitor,
  Factory,
  Store,
  Zap,
  Eye,
  Shield,
  BookOpen,
  TrendingUp,
  Award,
  HelpCircle,
  Rocket,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react';
import { navLinks } from '../data/landing-data';
import type { NavLink } from '../data/landing-data';

const iconMap: Record<string, LucideIcon> = {
  Layers,
  FileText,
  LineChart,
  Ship,
  BarChart3,
  Bot,
  BadgeDollarSign,
  Truck,
  Monitor,
  Factory,
  Store,
  Zap,
  Eye,
  Shield,
  BookOpen,
  TrendingUp,
  Award,
  HelpCircle,
  Rocket,
  MessageCircle,
};
import { useThemeStore } from '@/stores/theme-store';
import { LogoPrixia } from '@/components/layout/logo-prixia';

function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useThemeStore();

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const toggle = useCallback(() => {
    // Add transition class before switching
    document.documentElement.classList.add('theme-transition');
    setTheme(isDark ? 'light' : 'dark');
    // Remove transition class after animation completes
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 350);
  }, [isDark, setTheme]);

  return (
    <button
      onClick={toggle}
      className={`relative w-9 h-9 rounded-lg flex items-center justify-center
        text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white
        hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
    >
      <motion.div
        key={isDark ? 'moon' : 'sun'}
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        exit={{ scale: 0, rotate: 90 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        {isDark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
      </motion.div>
    </button>
  );
}

function DesktopDropdown({
  link,
  onNavigate,
}: {
  link: NavLink;
  onNavigate: (href: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
        onClick={() => setOpen(!open)}
      >
        {link.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && link.dropdown && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-5 min-w-[360px]">
            <div
              className={`grid gap-6 ${link.dropdown.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
            >
              {link.dropdown.map((group) => (
                <div key={group.title}>
                  <h4 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">
                    {group.title}
                  </h4>
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const ItemIcon = item.icon ? iconMap[item.icon] : null;
                      return (
                        <a
                          key={item.href + item.label}
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            setOpen(false);
                            onNavigate(item.href);
                          }}
                          className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-1.5 px-2 -mx-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          {ItemIcon && (
                            <ItemIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          )}
                          {item.label}
                        </a>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function LandingNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navigate = useNavigate();

  const location = useLocation();

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setMobileDropdownOpen(null);
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        // Already on landing — just scroll
        const el = document.querySelector(href);
        el?.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Navigate to landing page with hash
        navigate('/' + href);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 dark:bg-gray-950/90 backdrop-blur-md shadow-sm border-b border-gray-100 dark:border-gray-800'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <LogoPrixia className="h-9 w-auto" />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.dropdown ? (
                <DesktopDropdown key={link.label} link={link} onNavigate={handleNavClick} />
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }}
                  className="text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors px-4 py-2"
            >
              Iniciar Sesion
            </Link>
            <RegistroCta className="text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 px-4 py-2 rounded-lg transition-all hover:shadow-[0_0_30px_rgba(99,102,241,0.3)] dark:hover:shadow-[0_0_30px_rgba(99,102,241,0.4)]">
              Comenzar Gratis
            </RegistroCta>
          </div>

          {/* Mobile toggle */}
          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-4 border-t border-gray-100 dark:border-gray-800"
          >
            <div className="flex flex-col gap-1 pt-4">
              {navLinks.map((link) =>
                link.dropdown ? (
                  <div key={link.label}>
                    <button
                      onClick={() =>
                        setMobileDropdownOpen(mobileDropdownOpen === link.label ? null : link.label)
                      }
                      className="flex items-center justify-between w-full text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          mobileDropdownOpen === link.label ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {mobileDropdownOpen === link.label && link.dropdown && (
                      <div className="ml-3 pl-3 border-l border-gray-200 dark:border-gray-800 space-y-3 py-2">
                        {link.dropdown.map((group) => (
                          <div key={group.title}>
                            <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-1">
                              {group.title}
                            </h4>
                            {group.items.map((item) => {
                              const ItemIcon = item.icon ? iconMap[item.icon] : null;
                              return (
                                <a
                                  key={item.href + item.label}
                                  href={item.href}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleNavClick(item.href);
                                  }}
                                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                  {ItemIcon && (
                                    <ItemIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  )}
                                  {item.label}
                                </a>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    {link.label}
                  </a>
                )
              )}
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-100 dark:border-gray-800 mt-2">
                <Link
                  to="/login"
                  className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white px-3 py-2 text-center"
                >
                  Iniciar Sesion
                </Link>
                <RegistroCta className="text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-2 rounded-lg text-center">
                  Comenzar Gratis
                </RegistroCta>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}
